import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useSenderEnvelope } from 'services/repositories/transactions/transactions';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { PartnerDTO, PriceCatalogRoutes, SenderEnvelopeDTO, TransactionsRoutes } from 'support/types';
import { Page } from 'components/Page/Page';
import { Placeholder } from 'components/Placeholder/Placeholder';
import { FieldAnnotationPopover } from 'components/WebEDI/FieldAnnotationPopover/FieldAnnotationPopover';
import { WebEDISidebar } from 'components/WebEDI/WebEDISidebar/WebEDISidebar';

import { DroppedSenderEnvelopeAlert } from '../DroppedSenderEnvelopeAlert/DroppedSenderEnvelopeAlert';
import { SenderEnvelopeErrorBanner } from './SenderEnvelopeErrorBanner/SenderEnvelopeErrorBanner';
import { SenderEnvelopeHeader } from './SenderEnvelopeHeader/SenderEnvelopeHeader';

export function SenderEnvelopeViewPage() {
  const { id } = useParams<{ id: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { data: senderEnvelopeData, isLoading: isLoadingSenderEnvelope } = useSenderEnvelope({
    variables: {
      senderEnvelopeId: id,
    },
  });

  const isLoading = isLoadingSenderEnvelope;
  return (
    <SenderEnvelopeUI
      isLoading={isLoading}
      senderEnvelope={senderEnvelopeData?.transaction}
      currentPartner={currentPartner}
    />
  );
}

export const SenderEnvelopeUI = ({
  isLoading,
  senderEnvelope,
  currentPartner,
}: {
  isLoading: boolean;
  senderEnvelope: SenderEnvelopeDTO | undefined;
  currentPartner: PartnerDTO | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <Page isLoading={isLoading}>
      {senderEnvelope && <SenderEnvelopeHeader senderEnvelope={senderEnvelope} />}
      <SchemableFormNavigationContextProvider<WebEDIAnnotation>
        service={WebEDIErrorsServiceSingleton}
        NavigableItemPopoverComponent={FieldAnnotationPopover}
      >
        {senderEnvelope && (
          <Page.Section
            testId="sender-envelope-view-page-content"
            topBanner={
              <>
                <DroppedSenderEnvelopeAlert senderEnvelope={senderEnvelope} />
                {senderEnvelope ? <SenderEnvelopeErrorBanner senderEnvelope={senderEnvelope} /> : null}
              </>
            }
            rightAside={
              <WebEDISidebar
                document={senderEnvelope}
                documentOriginApp={null}
                relatedTransactions={[senderEnvelope]}
                currentPartner={currentPartner}
                isSenderEnvelope
              />
            }
          >
            <Placeholder title={t('webedi:senderEnvelope.placeholder.title')} icon={<EyeSlashIcon strokeWidth="1" />} />
          </Page.Section>
        )}
      </SchemableFormNavigationContextProvider>
    </Page>
  );
};

SenderEnvelopeViewPage.loader = createLoader(
  ({ params }) => useSenderEnvelope.getKey({ senderEnvelopeId: params.id }),
  useSenderEnvelope.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useSenderEnvelope.queryFn>>;
SenderEnvelopeViewPage.handle = createHandle<
  ResponseType,
  RouteParams<TransactionsRoutes.viewSenderEnvelope | PriceCatalogRoutes.viewSenderEnvelope>
>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:viewDocument.breadcrumb', {
      externalReference: data?.transaction.externalMessageRef ?? EMPTY_FIELD,
      documentType: data?.transaction.documentType
        ? t(`common:messageTypes.singular.${data.transaction.documentType}`)
        : t('webedi:senderEnvelope.breadcrumb.unknownDocumentType'),
    }),
  to: routeToPage(TransactionsRoutes.viewSenderEnvelope, { id: params.id }),
  key: `sender-envelope`,
}));

/**
 * i18n keys
 * t('webedi:viewDocument.breadcrumb', { externalReference, documentType })
 * t('webedi:senderEnvelope.breadcrumb.unknownDocumentType')
 * The `common:messageTypes.singular.*` branch is a template literal; its expansions are enumerated
 * centrally in src/support/helpers/transactions/transactions.ts (legacy pattern — do not extend).
 */
